/*
  EVA AUDIO POLICY -- the single arbiter of MUSIC playback (2026-08-26).

  THE BUG THIS FIXES. Opening the Kallare TV while the Radio was playing
  left BOTH running: the EVATV menu song (and then film audio) on top of
  a tape or a station. Two music sources at once, live on the site.

  WHY A CENTRAL POLICY AND NOT A PATCH AT EACH CALL SITE. There are five
  separate places music can start (radio tape play, radio station play,
  EVATV song start, EVATV song resume from the MENU button, film select)
  spread across three files that do not import each other. Patching
  "stop the other one" into each of them is N-squared by construction:
  every new source added later has to remember to pause every existing
  source, and every existing source has to be edited to know about it.
  Instead each source REGISTERS itself once here and declares how to
  pause itself; starting music is a claim() against this module, and the
  module pauses whatever else holds the floor. Adding a sixth source
  later means one register() call and no edits anywhere else.

  THE RULE. Exactly one MUSIC source plays at a time:
    - 'radio'      -- Eva Radio: tape playback AND station playback.
                      Two <audio> elements, but ONE logical source; the
                      radio's own internal tape/station exclusivity is
                      its own business, not this module's.
    - 'kallare-tv' -- the Basement TV: the EVATV menu song AND film
                      audio (YouTube player or plain Vimeo iframe).
  Starting either pauses the other.

  WHAT IS *NOT* MUSIC, and is therefore untouched by this module:
  UI hover/click blips, the tape deck's mechanical clacks, the VHS
  insert clunk, the room ambience by the small TV, static bursts
  between stations. Those are sound effects -- they are allowed to
  overlap music, they always have, and nothing here registers them.

  TV OUTRANKS RADIO. Ranks below. A claim from a real user action always
  wins, in both directions -- press PLAY on the radio while a film is
  running and the film stops; that is what Per asked for. The rank only
  decides AUTOMATIC claims: music that starts without the visitor
  pressing anything (the radio's cross-navigation resume, an autoplay
  retry). An automatic claim cannot take the floor from a
  higher-or-equal-ranked holder, so arriving in the Basement with the TV
  up can never be hijacked by the radio resuming itself underneath it.

  DELIBERATE CHOICE, EASY TO REVERSE -- a paused source does NOT come
  back on its own. Close the TV and the radio stays silent until you
  press PLAY; it does not resume mid-tape. This is a judgment call, not
  a technical limit: auto-resume is genuinely worse on a phone (sound
  reappearing from a widget that may be scrolled off-screen, with no
  obvious thing to press to make it stop), and it competes with iOS's
  own autoplay gesture rules. If Per wants the opposite, the change is
  local to this file: remember the displaced holder in takeFloor() and
  call a resume() callback on release. Nothing at the call sites changes.
*/
(function () {
  'use strict';

  var RANK = { 'kallare-tv': 2, 'radio': 1 };

  // Has MUSIC actually started this page-load? Deliberately a plain
  // in-memory var -- NOT localStorage, NOT sessionStorage. This is the
  // deckLoaded precedent from eva-radio.js: a persisted flag is exactly
  // what caused the phantom-tape bug, where state restored from an
  // earlier visit convinced the deck a tape was loaded when nothing had
  // been inserted this time. A visitor who cold-loads the site has, by
  // definition, not started any music yet, so this must start false on
  // every real page load. It survives PJAX navigation for free (this
  // script is never torn down), which is the correct scope: one visit,
  // one answer.
  var musicStarted = false;

  var sources = {};   // id -> { rank, pause, elements }
  var holder = null;  // id of whoever currently owns music playback
  var arbitrating = false; // re-entrancy guard -- see pauseOthers()

  function rankOf(id) {
    return Object.prototype.hasOwnProperty.call(RANK, id) ? RANK[id] : 0;
  }

  // Pausing a source fires 'pause' on its <audio> elements, which our own
  // backstop listeners below would otherwise read as "that source gave up
  // the floor" and use to clear a holder we are in the middle of setting.
  // The guard makes the whole take-the-floor step atomic from the
  // listeners' point of view.
  function pauseOthers(exceptId) {
    arbitrating = true;
    try {
      Object.keys(sources).forEach(function (id) {
        if (id === exceptId) { return; }
        try { sources[id].pause(); } catch (e) {}
      });
    } finally {
      arbitrating = false;
    }
  }

  window.EvaAudioPolicy = {
    /*
      Declare a music source.
        id       -- 'radio' | 'kallare-tv'
        opts.pause    -- REQUIRED. Silence every music surface this source
                         owns. Must be safe to call at any time, including
                         when the source is already silent or its player
                         does not exist yet.
        opts.elements -- optional <audio> elements belonging to this
                         source. Their native 'play' events become a
                         backstop automatic claim, so a playback path
                         that was never routed through claim() by hand
                         still cannot end up playing over another source.
                         Belt and braces on purpose: the explicit claims
                         at the call sites are the primary mechanism, but
                         this file should not depend on a future edit
                         elsewhere remembering to add one.
    */
    register: function (id, opts) {
      if (!id || !opts || typeof opts.pause !== 'function') { return; }
      sources[id] = {
        rank: rankOf(id),
        pause: opts.pause,
        elements: opts.elements || []
      };

      (opts.elements || []).forEach(function (el) {
        if (!el || !el.addEventListener) { return; }
        el.addEventListener('play', function () {
          if (arbitrating) { return; }
          window.EvaAudioPolicy.claim(id, { auto: true });
        });
        // Matching backstop for giving the floor UP. Harmless on the
        // brief pause/play a track change produces (it releases and
        // immediately re-claims), and it means a stop path that was
        // never routed through release() by hand still leaves the floor
        // free rather than permanently blocking automatic claims.
        el.addEventListener('pause', function () {
          if (arbitrating) { return; }
          window.EvaAudioPolicy.release(id);
        });
      });
    },

    /*
      Take the floor for `id`. Call this immediately BEFORE starting
      music, not after.
        opts.auto -- true for music starting without a user action
                     (cross-navigation resume, autoplay retry). An
                     automatic claim is refused when a
                     higher-or-equal-ranked source already holds the
                     floor; see TV OUTRANKS RADIO above.
      Returns true when the caller may proceed, false when refused.
      A refused caller is expected to not start -- and if it already
      started (the backstop path), it is paused here so the one-source
      rule still holds.
    */
    claim: function (id, opts) {
      if (!id) { return true; } // unknown caller: never block audio on a bug in this file
      var auto = !!(opts && opts.auto);

      if (holder === id) { return true; }

      if (auto && holder && rankOf(holder) >= rankOf(id)) {
        // Refused. If the claimant already began playing (backstop
        // route), silence it -- otherwise refusing would leave exactly
        // the two-sources-at-once state this module exists to prevent.
        if (sources[id]) {
          arbitrating = true;
          try { sources[id].pause(); } catch (e) {} finally { arbitrating = false; }
        }
        return false;
      }

      pauseOthers(id);
      holder = id;
      return true;
    },

    // The source stopped by itself (visitor pressed pause/stop/eject).
    // Only clears the floor if this source actually held it, so a stale
    // release from a source that was already displaced cannot blank out
    // whoever legitimately took over.
    release: function (id) {
      if (arbitrating) { return; }
      if (holder === id) { holder = null; }
    },

    holder: function () { return holder; },

    // Called ONLY from the two places music genuinely begins: the tape
    // element's own 'play' event, and a real station tune attempt. Not
    // page load, not the RADIO toggle, not mute, not any pointerdown.
    noteMusicStarted: function () { musicStarted = true; },
    hasMusicStarted: function () { return musicStarted; }
  };
})();
